/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Alert, message } from 'antd'
import { useEffect, useRef, useState } from 'react'
import BaseModal from 'src/components/ts-components/BaseModal'
import CurrencyInput from 'src/components/ts-components/CurrencyInput'
import { TextAreaInput } from 'src/components/ts-components/TextAreaInput'
import Price from 'src/components/ts-components/Price'
import { formatCurrency } from '@sportninja/common/currency'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import colors from '@sportninja/common/constants/appColors'
import type {
  AdditionalInvoiceBody,
  BalanceLineKind,
  BalanceRegistration,
  BalanceSlot,
} from '@sportninja/common/services/teamBalance'
import { useAdditionalInvoicePreview } from '@sportninja/common/features/team-balance/hooks/useAdditionalInvoicePreview'
import {
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'
import { dollarsToMinor } from '../utils/balanceMath'
import { fieldGuardMessage, guardMessage } from '../utils/balanceError'
import { balanceLabel, balanceText } from '../utils/balanceI18n'
import {
  addInvoicePreviewState,
  canConfirmAddInvoice,
} from '../utils/addInvoicePreviewState'
import { isOfficialLine, lineName } from '../utils/balanceRowKind'
import AddInvoicePreviewSkeleton from './AddInvoicePreviewSkeleton'
import OfficialEffectList from './OfficialEffectList'

const AMOUNT_FIELD = 'amount'
const DESCRIPTION_FIELD = 'description'
/** The server's column limit, so the counter and the API agree. */
const DESCRIPTION_MAX_LENGTH = 255

type AddInvoiceModalProps = {
  open: boolean
  teamId: string
  /** D40 — the sheet's roster; the preview and the add are roster-scoped. */
  rosterId: string
  slot: BalanceSlot | null
  registration: Pick<BalanceRegistration, 'currency'> | null
  submitting: boolean
  onConfirm: (body: AdditionalInvoiceBody) => Promise<unknown>
  onClose: () => void
  /**
   * Sep 23 (T6) — `official` for a team official's line: the extra comes out of the
   * uninvoiced player shares, and the preview lists what it does to them.
   */
  expectedKind?: BalanceLineKind
}

/**
 * SN-8310 (Sep 22) — Add Invoice: one more charge on an INVOICED player's share,
 * due today with tax on top. The charge shown is the SERVER's exact preview (the
 * same row the add writes); nothing is priced here. The confirm carries the
 * preview's fingerprint, so a charge that changed since it was shown is refused
 * and the preview refetched rather than submitted blind.
 */
const AddInvoiceModal = ({
  open,
  teamId,
  rosterId,
  slot,
  registration,
  submitting,
  onConfirm,
  onClose,
  expectedKind,
}: AddInvoiceModalProps) => {
  'use memo'
  const [amount, setAmount] = useState<number | null>(null)
  const [description, setDescription] = useState('')
  const [amountError, setAmountError] = useState<string | null>(null)
  const [descriptionError, setDescriptionError] = useState<string | null>(null)
  // A second click lands before `submitting` re-renders the button disabled.
  const submittingRef = useRef(false)

  useEffect(() => {
    if (open) {
      setAmount(null)
      setDescription('')
      setAmountError(null)
      setDescriptionError(null)
    }
    // Once per OPEN, never on `slot`: it is re-resolved on every refetch, and a
    // background refresh must never wipe what the rep has typed.
  }, [open])

  const currencyCode = registration?.currency?.code ?? 'CAD'
  const amountCents =
    amount !== null && Number.isFinite(amount) ? dollarsToMinor(amount) : 0

  const { preview, previewCents, settling, isFetching, error, refetch } =
    useAdditionalInvoicePreview({
      teamId,
      rosterId,
      slotId: open && slot ? slot.id : null,
      amountCents,
      expectedKind,
    })

  // An amount the server refuses belongs under the amount, not in the panel.
  const previewAmountError = error
    ? fieldGuardMessage(error, 'amount_cents')
    : null
  const shownAmountError = amountError ?? previewAmountError
  const previewState = addInvoicePreviewState({
    amountCents,
    settling,
    isFetching,
    hasPreview: preview !== null,
    hasError: Boolean(error),
    isAmountError: previewAmountError !== null,
  })
  const canConfirm = canConfirmAddInvoice(
    previewState,
    isFetching,
    preview?.fingerprint
  )
  const playerName = slot ? lineName(slot) ?? '' : ''
  const money = (dollars: number) =>
    formatCurrency({ amount: dollarsToMinor(dollars), currencyCode })

  const submit = () => {
    const fingerprint = preview?.fingerprint
    if (!canConfirm || !fingerprint || submittingRef.current) {
      return
    }
    submittingRef.current = true
    setAmountError(null)
    setDescriptionError(null)
    onConfirm({
      // The amount the preview was priced for, which `settling` guarantees is
      // the amount on screen.
      amount_cents: previewCents,
      description: description.trim() ? description.trim() : null,
      preview_fingerprint: fingerprint,
      ...(expectedKind ? { expected_kind: expectedKind } : {}),
    })
      .catch((confirmError) => {
        const amountMessage = fieldGuardMessage(confirmError, 'amount_cents')
        const descriptionMessage = fieldGuardMessage(
          confirmError,
          DESCRIPTION_FIELD
        )
        if (amountMessage || descriptionMessage) {
          setAmountError(amountMessage)
          setDescriptionError(descriptionMessage)
          return
        }
        // Anything else (a stale preview, the slot changed): say why, then show
        // the charge as it stands now instead of letting the old one resubmit.
        message.error(guardMessage(confirmError))
        refetch()
      })
      .finally(() => {
        submittingRef.current = false
      })
  }

  const renderPreview = () => {
    if (previewState === 'enter_amount') {
      return (
        <p css={mutedTextStyle}>
          {balanceText('common:balanceAddInvoiceEnterAmount')}
        </p>
      )
    }
    if (previewState === 'loading') {
      return <AddInvoicePreviewSkeleton />
    }
    if (previewState === 'error') {
      return (
        <Alert
          type='warning'
          showIcon
          message={guardMessage(error)}
          data-testid='add-invoice-preview-error'
        />
      )
    }
    if (previewState === 'field_error' || !preview) {
      return null
    }
    return (
      <div css={previewStyle} data-testid='add-invoice-preview'>
        <div css={moneyRowStyle}>
          <span>{balanceLabel('common:balanceAddInvoiceCharge')}</span>
          <Price
            amount={dollarsToMinor(preview.subtotal)}
            currency={currencyCode}
          />
        </div>
        <div css={moneyRowStyle}>
          <span>
            {balanceLabel('common:balanceAddInvoiceTax', {
              rate: preview.tax_rate,
            })}
          </span>
          <Price amount={dollarsToMinor(preview.tax)} currency={currencyCode} />
        </div>
        <div css={[moneyRowStyle, totalRowStyle]}>
          <span>{balanceLabel('common:balanceColTotal')}</span>
          <Price
            amount={dollarsToMinor(preview.total)}
            currency={currencyCode}
          />
        </div>
        <p css={detailTextStyle} data-testid='add-invoice-due'>
          {balanceText('common:balanceAddInvoiceDue', {
            // A calendar day at midnight UTC, so it is read in UTC.
            date: formatShortDate(preview.due_date),
          })}
        </p>
        <p css={detailTextStyle} data-testid='add-invoice-new-share'>
          {balanceText('common:balanceAddInvoiceNewShare', {
            name: playerName,
            share: money(preview.new_share),
            was: money(preview.current_share),
          })}
        </p>
        <p css={detailTextStyle} data-testid='add-invoice-payment-route'>
          {balanceText(
            preview.can_pay_online
              ? 'common:balanceAddInvoicePayOnline'
              : 'common:balanceAddInvoicePayOffline'
          )}
        </p>
        {/* Sep 23 (T6) — an official's extra is fee money: what it takes from whom. */}
        {preview.player_pool_effect ? (
          <OfficialEffectList
            intro={balanceText('common:balanceOfficialAddInvoiceEffect')}
            effect={preview.player_pool_effect}
            currencyCode={currencyCode}
          />
        ) : null}
      </div>
    )
  }

  return (
    <BaseModal
      isOpen={open}
      title={balanceLabel('common:balanceAddInvoice')}
      okText={balanceLabel('common:balanceAddInvoice')}
      cancelText={balanceLabel('common:balanceCommsCancel')}
      disableOk={!canConfirm}
      isLoading={submitting}
      onClose={onClose}
      onOk={submit}
      desktopWidth='520px'
      destroyOnClose
    >
      {slot === null ? null : (
        <div css={bodyStyle}>
          <p css={introStyle}>
            {balanceText('common:balanceAddInvoiceIntro', {
              name: playerName,
            })}
          </p>
          <CurrencyInput
            name={AMOUNT_FIELD}
            label={balanceLabel('common:balanceAddInvoiceAmount')}
            value={amount}
            country={registration?.currency?.country ?? undefined}
            onChange={(value) => {
              setAmount(Number.isFinite(value) ? value : null)
              setAmountError(null)
            }}
            fieldErrors={shownAmountError ? [AMOUNT_FIELD] : []}
            customError={
              shownAmountError ? { [AMOUNT_FIELD]: shownAmountError } : {}
            }
          />
          <div css={fieldStyle}>
            <TextAreaInput
              name={DESCRIPTION_FIELD}
              label={balanceLabel('common:balanceAddInvoiceDescription')}
              placeholder={balanceLabel(
                'common:balanceAddInvoiceDescriptionPlaceholder'
              )}
              value={description}
              setValue={(value) => {
                setDescription(value)
                setDescriptionError(null)
              }}
              maxLength={DESCRIPTION_MAX_LENGTH}
              rows={2}
              fieldErrors={descriptionError ? [DESCRIPTION_FIELD] : []}
              customError={
                descriptionError
                  ? { [DESCRIPTION_FIELD]: descriptionError }
                  : {}
              }
            />
            <p css={mutedTextStyle}>
              {balanceText('common:balanceAddInvoiceDescriptionHint')}
            </p>
          </div>
          {renderPreview()}
          {/* Sep 23 (T6) — an official's extra comes OUT of the team fee, so the note that
              a charge sits on top of the league's figures holds for a player's only. */}
          {slot && isOfficialLine(slot) ? null : (
            <p css={footnoteStyle} data-testid='add-invoice-targets-note'>
              {balanceText('common:balanceAddInvoiceTargetsNote')}
            </p>
          )}
        </div>
      )}
    </BaseModal>
  )
}

export default AddInvoiceModal

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const introStyle = css`
  margin: 0;
  ${paragraphSmall};
  color: ${colors.NEUTRAL_0};
`

const mutedTextStyle = css`
  margin: 0;
  ${paragraphSmall};
  color: ${colors.NEUTRAL_70};
`

/** Raised off the modal body (SECONDARY_300), like the sheet's own panels. */
const previewStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  background: ${colors.SECONDARY_200};
  ${paragraphSmall};
  color: ${colors.NEUTRAL_0};
`

const moneyRowStyle = css`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-variant-numeric: tabular-nums;
`

const totalRowStyle = css`
  ${weightSemiBold};
  padding-top: 8px;
  border-top: 1px solid ${colors.SECONDARY_100};
`

const detailTextStyle = css`
  margin: 0;
  color: ${colors.NEUTRAL_50};
`

const footnoteStyle = css`
  margin: 0;
  ${paragraphXSmall};
  color: ${colors.NEUTRAL_70};
`
